import { useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownToLine, ArrowUpFromLine, Landmark, Pencil, Plus, RefreshCw, Trash2, Wallet as WalletIcon } from "lucide-react";
import { walletApi, type WalletEntry } from "../lib/api";
import { formatCurrency, formatDate, formatSignedCurrency, fromLocalDatetimeInput, toLocalDatetimeInput } from "../lib/format";
import CashflowChart from "../components/CashflowChart";
import { useI18n } from "../lib/i18n";

export default function WalletPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ["wallet"], queryFn: () => walletApi.list() });
  const summaryQ = useQuery({ queryKey: ["wallet-summary"], queryFn: () => walletApi.summary() });
  const analyticsQ = useQuery({ queryKey: ["wallet-analytics"], queryFn: () => walletApi.analytics() });

  // The server owns this split so the wallet and the dashboard can never
  // disagree; fall back to "nothing saved" while the summary is still loading.
  const savedTotal = summaryQ.data?.savings_total ?? 0;
  const accountBalance = summaryQ.data?.account_balance ?? listQ.data?.balance ?? 0;

  const [addOpen, setAddOpen] = useState(false);

  const recalcM = useMutation({
    mutationFn: () => walletApi.recalculate(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["wallet-summary"] });
      qc.invalidateQueries({ queryKey: ["wallet-analytics"] });
    },
  });

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("nav.wallet")}</p>
          <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
            <WalletIcon className="w-6 h-6 text-accent" />
            {t("wallet.title")}
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => recalcM.mutate()}
            disabled={recalcM.isPending}
            className="btn-ghost"
          >
            <RefreshCw className={`w-4 h-4 ${recalcM.isPending ? "animate-spin" : ""}`} />
            {t("wallet.recalculate")}
          </button>
          <button type="button" onClick={() => setAddOpen(true)} className="btn-gold">
            <Plus className="w-4 h-4" />
            {t("wallet.addEntry")}
          </button>
        </div>
      </header>

      {/* Balance + summary */}
      <section className="grid gap-4 md:grid-cols-4">
        <StatCard
          label={t("wallet.balance")}
          value={formatCurrency(listQ.data?.balance ?? 0)}
          accent="text-accent"
          sub={
            /* Savings withdrawals skip the balance chain, so the headline still
               counts money already moved out. Split it only when that is
               actually the case -- with nothing saved the two are equal. */
            savedTotal > 0 ? (
              <span className="flex flex-wrap gap-x-2 gap-y-0.5">
                <span>{t("wallet.inAccount", { amount: formatCurrency(accountBalance) })}</span>
                <span className="text-accent">
                  {t("wallet.savedTotal", { amount: formatCurrency(savedTotal) })}
                </span>
              </span>
            ) : undefined
          }
        />
        <StatCard
          label={t("wallet.netDeposited")}
          value={formatCurrency(analyticsQ.data?.summary.net_deposited)}
          sub={
            summaryQ.data
              ? t("wallet.inOut", {
                  in: formatCurrency(summaryQ.data.deposits_total),
                  out: formatCurrency(summaryQ.data.withdrawals_total),
                })
              : undefined
          }
        />
        <StatCard
          label={t("wallet.realReturn")}
          value={formatSignedCurrency(analyticsQ.data?.summary.real_return ?? 0)}
          accent={
            (analyticsQ.data?.summary.real_return ?? 0) >= 0
              ? "text-pnl-pos"
              : "text-pnl-neg"
          }
        />
        <StatCard
          label={t("wallet.roi")}
          value={
            analyticsQ.data?.summary.roi_pct != null
              ? `${analyticsQ.data.summary.roi_pct.toFixed(2)}%`
              : "—"
          }
          accent="text-accent"
          /* ROI has no meaning once net deposited goes negative: dividing a
             gain by a negative base prints a large NEGATIVE percentage, which
             reads as a loss. Say why instead of leaving a bare dash. */
          sub={
            /* A node, not a string: StatCard truncates strings to one line and
               this sentence needs to wrap. */
            analyticsQ.data && analyticsQ.data.summary.roi_pct == null
              ? <span className="block leading-snug">{t("wallet.roiNa")}</span>
              : undefined
          }
        />
      </section>

      {/* Chart */}
      <section className="glass p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-parchment">{t("wallet.cashflowOverTime")}</h2>
        </div>
        {analyticsQ.data && <CashflowChart series={analyticsQ.data.series} />}
      </section>

      {addOpen && (
        <AddEntryForm
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["wallet"] });
            qc.invalidateQueries({ queryKey: ["wallet-summary"] });
            qc.invalidateQueries({ queryKey: ["wallet-analytics"] });
            qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
            setAddOpen(false);
          }}
        />
      )}

      {/* Entries list */}
      <section className="glass p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-parchment">{t("wallet.entries")}</h2>
          <span className="text-parchment-muted text-xs">
            {t("wallet.totalCount", { count: listQ.data?.entries.length ?? 0 })}
          </span>
        </div>

        {listQ.isLoading && <p className="text-parchment-muted text-sm">{t("common.loading")}</p>}
        {listQ.isError && (
          <p className="text-pnl-neg text-sm">
            {(listQ.error as Error)?.message ?? t("wallet.loadError")}
          </p>
        )}
        {!listQ.isLoading && (listQ.data?.entries.length ?? 0) === 0 && (
          <p className="text-parchment-muted text-sm py-6 text-center">
            {t("wallet.empty")}
          </p>
        )}

        <ul className="divide-y divide-glass">
          {(listQ.data?.entries ?? []).map((e) => (
            <EntryRow key={e.id} entry={e} />
          ))}
        </ul>
      </section>
    </>
  );
}

function StatCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  /** A plain string is truncated to one line; a node is allowed to wrap. */
  sub?: ReactNode;
  accent?: string;
}) {
  return (
    <div className="glass p-4">
      <p className="label">{label}</p>
      <p className={`mt-2 text-2xl font-bold tabular ${accent ?? "text-parchment"}`}>{value}</p>
      {sub && (
        <p
          className={
            "text-xs text-parchment-muted mt-1 " + (typeof sub === "string" ? "truncate" : "")
          }
        >
          {sub}
        </p>
      )}
    </div>
  );
}

function EntryRow({ entry }: { entry: WalletEntry }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const destroyM = useMutation({
    mutationFn: () => walletApi.destroy(entry.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["wallet-summary"] });
    },
  });

  const isDeposit = entry.type === "deposit";
  // Money banked as savings is not a loss -- it reads gold, like the rest of
  // the savings language in the app, rather than red like a spent withdrawal.
  const isSavingWithdrawal = !isDeposit && entry.is_saving;
  const Icon = isDeposit ? ArrowDownToLine : isSavingWithdrawal ? Landmark : ArrowUpFromLine;
  const signCls = isDeposit
    ? "text-pnl-pos"
    : isSavingWithdrawal
      ? "text-accent"
      : "text-pnl-neg";

  return (
    <li className="py-3 flex items-center gap-4">
      <span
        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border ${
          isDeposit
            ? "bg-emerald-500/10 border-emerald-500/25 text-pnl-pos"
            : isSavingWithdrawal
              ? "bg-gold-500/15 border-gold-500/25 text-accent"
              : "bg-rose-500/10 border-rose-500/25 text-pnl-neg"
        }`}
      >
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-parchment capitalize">{entry.type}</span>
          {entry.is_saving && (
            <span className="chip-gold text-[10px] px-2 py-0.5">{t("wallet.savingChip")}</span>
          )}
        </div>
        <p className="text-parchment-muted text-xs">{formatDate(entry.happened_at)}</p>
        {entry.notes && <p className="text-parchment-muted text-xs mt-0.5 truncate">{entry.notes}</p>}
      </div>
      <div className={`text-end tabular font-semibold ${signCls}`}>
        {formatSignedCurrency(isDeposit ? entry.amount : -entry.amount)}
      </div>
      <div className="hidden sm:block text-end w-28 tabular text-parchment-muted text-xs">
        {entry.balance_after != null ? formatCurrency(entry.balance_after) : "—"}
      </div>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-parchment-muted hover:text-accent"
        title={t("wallet.edit")}
        aria-label={t("wallet.edit")}
      >
        <Pencil className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => {
          if (confirm(t("wallet.confirmDelete"))) destroyM.mutate();
        }}
        disabled={destroyM.isPending}
        className="text-parchment-muted hover:text-pnl-neg disabled:opacity-40"
        title={t("common.delete")}
      >
        <Trash2 className="w-4 h-4" />
      </button>

      {editing && <EditEntryModal entry={entry} onClose={() => setEditing(false)} />}
    </li>
  );
}

/**
 * Popup editor for a deposit / withdrawal: amount plus the date AND hour it
 * happened. The datetime-local input carries both, and the value round-trips
 * through the same local<->ISO helpers the add form uses, so an entry edited
 * here lands on the same instant the user sees.
 */
function EditEntryModal({ entry, onClose }: { entry: WalletEntry; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [amount, setAmount] = useState(String(entry.amount));
  const [happenedAt, setHappenedAt] = useState(toLocalDatetimeInput(entry.happened_at));
  const [isSaving, setIsSaving] = useState(entry.is_saving);
  const [error, setError] = useState<string | null>(null);

  const isWithdrawal = entry.type === "withdrawal";

  const m = useMutation({
    mutationFn: () =>
      walletApi.update(entry.id, {
        amount: parseFloat(amount),
        happened_at: fromLocalDatetimeInput(happenedAt),
        // Only a withdrawal can be a saving; sending it for a deposit would
        // let a stale flag ride along if the row was edited after a type change.
        ...(isWithdrawal ? { is_saving: isSaving } : {}),
      }),
    onSuccess: () => {
      // Moving an entry in time re-orders the ledger, so every balance-derived
      // view has to be refetched, not just the list.
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["wallet-summary"] });
      qc.invalidateQueries({ queryKey: ["wallet-analytics"] });
      qc.invalidateQueries({ queryKey: ["journey"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      onClose();
    },
    onError: (e) => setError((e as Error).message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError(t("wallet.amountError"));
      return;
    }
    if (!happenedAt) {
      setError(t("wallet.dateError"));
      return;
    }
    m.mutate();
  }

  // Portalled to <body>: this row lives inside a .glass section, whose
  // backdrop-blur creates a containing block that would otherwise trap a
  // position:fixed overlay inside the card instead of covering the viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-end sm:place-items-center p-4"
      style={{ backgroundColor: "rgb(var(--shadow) / 0.5)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("wallet.editEntry")}
        className="w-full sm:max-w-md rounded-2xl p-5 shadow-2xl"
        style={{
          backgroundColor: "rgb(var(--bg-800))",
          border: "1px solid rgb(var(--border-mid))",
          boxShadow: "var(--shadow-glass-hover)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-parchment">{t("wallet.editEntry")}</h2>
          <button type="button" onClick={onClose} className="text-parchment-muted text-sm hover:text-parchment">
            {t("common.cancel")}
          </button>
        </div>

        <p className="text-xs text-parchment-muted mb-4 capitalize">{entry.type}</p>

        <form onSubmit={onSubmit} className="grid gap-4">
          <label className="grid gap-2">
            <span className="label">{t("wallet.amount")}</span>
            <input
              type="number"
              min="0.01"
              step="any"
              required
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="input"
            />
          </label>

          <label className="grid gap-2">
            <span className="label">{t("wallet.dateTime")}</span>
            <input
              type="datetime-local"
              step="1"
              required
              value={happenedAt}
              onChange={(e) => setHappenedAt(e.target.value)}
              className="input"
            />
          </label>

          {/* Same control as the add form — without it, a withdrawal could be
              marked as saving on creation but never corrected afterwards.
              Toggling it moves money in and out of the balance chain, which
              the onSuccess invalidations below already account for. */}
          {isWithdrawal && (
            <label className="flex items-center gap-2 text-sm text-parchment">
              <input
                type="checkbox"
                checked={isSaving}
                onChange={(e) => setIsSaving(e.target.checked)}
                className="accent-gold-400"
              />
              <span>
                {t("wallet.markSavingPre")} <strong>{t("wallet.savingWord")}</strong>{" "}
                {t("wallet.markSavingPost")}
              </span>
            </label>
          )}

          {error && (
            <p className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={m.isPending} className="btn-gold">
              {m.isPending ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

function AddEntryForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const [type, setType] = useState<"deposit" | "withdrawal">("deposit");
  const [amount, setAmount] = useState("");
  const [happenedAt, setHappenedAt] = useState(toLocalDatetimeInput(new Date().toISOString()));
  const [isSaving, setIsSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () =>
      walletApi.create({
        type,
        amount: parseFloat(amount),
        happened_at: fromLocalDatetimeInput(happenedAt),
        is_saving: isSaving,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => onSaved(),
    onError: (e) => setError((e as Error).message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError(t("wallet.amountError"));
      return;
    }
    m.mutate();
  }

  return (
    <section className="glass p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-parchment">{t("wallet.newEntry")}</h2>
        <button type="button" onClick={onClose} className="text-parchment-muted text-sm hover:text-parchment">
          {t("common.cancel")}
        </button>
      </div>

      <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
        <div>
          <span className="label mb-2 block">{t("wallet.type")}</span>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setType("deposit")}
              className={
                "rounded-xl border px-3 py-2.5 text-sm font-semibold transition " +
                (type === "deposit"
                  ? "border-emerald-400/60 bg-emerald-500/15 text-pnl-pos"
                  : "border-glass-mid bg-surface-1 text-parchment-muted hover:bg-surface-2")
              }
            >
              {t("wallet.deposit")}
            </button>
            <button
              type="button"
              onClick={() => setType("withdrawal")}
              className={
                "rounded-xl border px-3 py-2.5 text-sm font-semibold transition " +
                (type === "withdrawal"
                  ? "border-rose-400/60 bg-rose-500/15 text-pnl-neg"
                  : "border-glass-mid bg-surface-1 text-parchment-muted hover:bg-surface-2")
              }
            >
              {t("wallet.withdrawal")}
            </button>
          </div>
        </div>

        <label className="grid gap-2">
          <span className="label">{t("wallet.amountUsd")}</span>
          <input
            type="number"
            step="0.01"
            min="0.01"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="input"
            placeholder={t("wallet.amountPlaceholder")}
          />
        </label>

        <label className="grid gap-2">
          <span className="label">{t("wallet.when")}</span>
          <input
            type="datetime-local"
            step="1"
            required
            value={happenedAt}
            onChange={(e) => setHappenedAt(e.target.value)}
            className="input"
          />
        </label>

        {type === "withdrawal" && (
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-parchment">
            <input
              type="checkbox"
              checked={isSaving}
              onChange={(e) => setIsSaving(e.target.checked)}
              className="accent-gold-400"
            />
            <span>
              {t("wallet.markSavingPre")} <strong>{t("wallet.savingWord")}</strong>{" "}
              {t("wallet.markSavingPost")}
            </span>
          </label>
        )}

        <label className="grid gap-2 sm:col-span-2">
          <span className="label">{t("wallet.notes")}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="input"
            placeholder={t("common.optional")}
          />
        </label>

        {error && (
          <p className="sm:col-span-2 rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
            {error}
          </p>
        )}

        <div className="sm:col-span-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={m.isPending} className="btn-gold">
            {m.isPending ? t("common.saving") : t("wallet.addEntry")}
          </button>
        </div>
      </form>
    </section>
  );
}
